import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCcw, Send, Filter, Loader2, Users } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

// Firm admin inbox for the Family CFO Plan module.
// Two collections combined: enquiries (from public landing) + intakes
// (started the wizard). Filters + inline status change + one-click Prosaic
// invite once the client has paid.

const STATUS_LABELS = {
    enquiry_received: "Enquiry received",
    suitability_check: "Suitability check",
    intake_invite_sent: "Intake invite sent",
    intake_in_progress: "Intake in progress",
    waiting_for_documents: "Waiting for documents",
    intake_completed: "Intake completed",
    initial_review: "Initial review",
    missing_information_requested: "Missing info requested",
    snapshot_prepared: "Snapshot prepared",
    health_score_prepared: "Health score prepared",
    debt_roadmap_prepared: "Debt roadmap prepared",
    plan_drafted: "Plan drafted",
    client_meeting_required: "Client meeting required",
    client_meeting_completed: "Client meeting completed",
    plan_approved: "Plan approved",
    dashboard_activated: "Dashboard activated",
    monthly_monitoring_active: "Monthly monitoring active",
    on_hold: "On hold",
    completed: "Completed",
    cancelled: "Cancelled",
};

export default function FirmFamilyCfo() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [statuses, setStatuses] = useState([]);
    const [kind, setKind] = useState("all");   // all | enquiry | intake
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (kind !== "all") params.set("kind", kind);
            if (statusFilter) params.set("status", statusFilter);
            const { data } = await api.get(`/family-cfo/admin/list?${params.toString()}`);
            setRows(data.rows || []);
            setSummary(data.summary || {});
            setStatuses(data.statuses || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [kind, statusFilter]);

    async function changeStatus(row, newStatus) {
        setBusy(`status-${row.id}`);
        try {
            await api.patch(`/family-cfo/admin/${row.id}/status`, { status: newStatus });
            toast.success(`Status set to ${STATUS_LABELS[newStatus] || newStatus}`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    }

    async function sendProsaicInvite(row) {
        if (!row.email) {
            toast.error("This record has no email address");
            return;
        }
        if (!window.confirm(`Send the Prosaic bank-feed invite email to ${row.email}?`)) return;
        setBusy(`prosaic-${row.id}`);
        try {
            await api.post(`/family-cfo/admin/${row.id}/send-prosaic-invite`);
            toast.success(`Prosaic invite sent to ${row.email}`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    }

    return (
        <div className="p-6 sm:p-8" data-testid="firm-family-cfo">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                        <Users className="h-3.5 w-3.5" /> Family CFO Plan
                    </div>
                    <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Family CFO inbox</h1>
                    <p className="mt-1 text-sm text-[#5E636E]">
                        Enquiries + in-progress intakes. Send the Prosaic invite once the client has paid their first fee.
                    </p>
                </div>
                <Button variant="outline" onClick={load} className="gap-1.5" data-testid="fc-refresh">
                    <RefreshCcw className="h-3.5 w-3.5" /> Refresh
                </Button>
            </div>

            {/* Summary */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
                <SummaryTile label="Total" value={summary.total ?? 0} testId="fc-tile-total" />
                <SummaryTile label="Enquiries" value={summary.enquiries ?? 0} testId="fc-tile-enquiries" />
                <SummaryTile label="Intakes" value={summary.intakes ?? 0} testId="fc-tile-intakes" />
                <SummaryTile label="In progress" value={summary.in_progress ?? 0} testId="fc-tile-inprogress" />
                <SummaryTile label="Monitoring" value={summary.monitoring_active ?? 0} testId="fc-tile-monitoring" />
            </div>

            {/* Filters */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
                <div className="text-xs text-[#5E636E] flex items-center gap-1.5"><Filter className="h-3.5 w-3.5" /> Filter</div>
                {["all", "intake", "enquiry"].map((k) => (
                    <Button
                        key={k}
                        variant={kind === k ? "default" : "outline"}
                        size="sm"
                        onClick={() => setKind(k)}
                        className={kind === k ? "bg-[#0F1115] text-white h-8" : "h-8"}
                        data-testid={`fc-kind-${k}`}
                    >
                        {k === "all" ? "All" : k === "intake" ? "Intakes" : "Enquiries"}
                    </Button>
                ))}
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="ml-2 rounded-md border border-[#E4E4E7] bg-white px-2 h-8 text-sm"
                    data-testid="fc-status-filter"
                >
                    <option value="">Any status</option>
                    {statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
                </select>
            </div>

            {/* Table */}
            <div className="mt-6 rounded-2xl border border-[#EAE7DE] bg-white overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-sm text-[#5E636E]">
                        <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#5E636E]">No records match this filter.</div>
                ) : (
                    <table className="w-full text-sm" data-testid="fc-table">
                        <thead className="bg-[#FBFAF6] text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                            <tr>
                                <th className="px-4 py-3 text-left">Client</th>
                                <th className="px-4 py-3 text-left">Kind</th>
                                <th className="px-4 py-3 text-left">Progress</th>
                                <th className="px-4 py-3 text-left">Status</th>
                                <th className="px-4 py-3 text-left">Created</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-[#EAE7DE]" data-testid={`fc-row-${r.id}`}>
                                    <td className="px-4 py-3 align-top">
                                        <div className="font-medium">{r.full_name || <span className="text-[#B7B3A6]">Not yet named</span>}</div>
                                        <div className="text-[12px] text-[#5E636E]">{r.email || "—"}</div>
                                        {r.mobile && <div className="text-[12px] text-[#5E636E]">{r.mobile}</div>}
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        <span className={
                                            "inline-block rounded-full px-2 py-0.5 text-[11px] " +
                                            (r._coll === "family_cfo_enquiries" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800")
                                        }>
                                            {r._coll === "family_cfo_enquiries" ? "Enquiry" : "Intake"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 align-top text-[13px] tabular-nums">
                                        {typeof r.progress_percent === "number" ? `${r.progress_percent}%` : "—"}
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        <select
                                            value={r.status}
                                            onChange={(e) => changeStatus(r, e.target.value)}
                                            disabled={busy === `status-${r.id}`}
                                            className="rounded-md border border-[#E4E4E7] bg-white px-2 h-8 text-[12.5px] max-w-[180px]"
                                            data-testid={`fc-row-status-${r.id}`}
                                        >
                                            {statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-4 py-3 align-top text-[12.5px] text-[#5E636E]">
                                        {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                                    </td>
                                    <td className="px-4 py-3 align-top text-right">
                                        {r._coll === "family_cfo_intakes" && r.email && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"
                                                onClick={() => sendProsaicInvite(r)}
                                                disabled={busy === `prosaic-${r.id}` || !!r.prosaic_invite_sent_at}
                                                data-testid={`fc-prosaic-${r.id}`}
                                            >
                                                {busy === `prosaic-${r.id}`
                                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                    : <Send className="h-3.5 w-3.5" />}
                                                {r.prosaic_invite_sent_at ? "Prosaic sent" : "Send Prosaic invite"}
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function SummaryTile({ label, value, testId }) {
    return (
        <div className="rounded-xl border border-[#EAE7DE] bg-white p-4" data-testid={testId}>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">{label}</div>
            <div className="mt-1 font-display text-2xl font-semibold tabular-nums">{value}</div>
        </div>
    );
}
